import type { RawBar } from './types.js';

const ATR_PERIOD = 14;

export const computeAtr14 = (bars: RawBar[]): number | null => {
    if (bars.length < ATR_PERIOD + 1) {
        return null;
    }

    const trueRanges: number[] = [];
    for (let index = 1; index < bars.length; index += 1) {
        const current = bars[index];
        const previousClose = bars[index - 1]?.c ?? current.c;
        trueRanges.push(
            Math.max(
                current.h - current.l,
                Math.abs(current.h - previousClose),
                Math.abs(current.l - previousClose),
            ),
        );
    }

    let atr = trueRanges.slice(0, ATR_PERIOD).reduce((sum, value) => sum + value, 0) / ATR_PERIOD;
    for (const tr of trueRanges.slice(ATR_PERIOD)) {
        atr = (atr * (ATR_PERIOD - 1) + tr) / ATR_PERIOD;
    }

    return Math.round(atr * 10_000) / 10_000;
};

// ATR expressed as a percentage of the current price, 2 decimals.
export const atrPct = (atr: number, price: number): number | null =>
    price > 0 ? Math.round((atr / price) * 10000) / 100 : null;

/**
 * Whether today's move away from the previous close exceeds one ATR, which the table badges
 * next to the symbol. Null when ATR or the previous close is unknown, or the move is within
 * a normal day's range.
 */
export const dailyMoveVsAtr = (
    currentPrice: number,
    atr14: number | null,
    prevDayClose: number | null,
): 'up' | 'down' | null => {
    if (atr14 == null || prevDayClose == null) return null;
    const dailyMove = currentPrice - prevDayClose;
    if (dailyMove > atr14) return 'up';
    if (dailyMove < -atr14) return 'down';
    return null;
};
